import { useLayoutEffect } from "react"
import { useLocalStorage, useSessionStorage } from "@mantine/hooks"
import { useMatch } from "react-router-dom"
import { useResponsiveSize } from "@/hooks"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { useCopilotStorage } from "@/components/CopilotAnalyze/hooks/useCopilotStorage"
import { useQueryClient } from "@tanstack/react-query"
import { useCopilotContext } from "@/components/CopilotAnalyze/Copilot.context"
import { copilotKeys } from "@/components/CopilotAnalyze/queries"
import type { CopilotChatsInterface } from "@/components/CopilotAnalyze/types"
import { uuidv7 } from "uuidv7"

const { QuorumDataType } = DjangIO.app.models

export const useToggleCopilotChat = () => {
    const { isMobile } = useResponsiveSize()
    const match = useMatch(":dataType/:profileId/*")
    const aiTrackingBoardMatch = useMatch("/ai_bill_tracker/:boardId/review_topic/*")
    const params = match?.params
    const context = useCopilotContext()
    const queryClient = useQueryClient()

    const { isOpen, entryPoint, setIsOpen, setEntryPoint } = useCopilotStorage()

    // Local Storage persists between sessions
    const [defaultOpenCopilotAnalyze, setDefaultOpenCopilotAnalyze] = useLocalStorage({
        key: "copilot-analyze-default-open",
        defaultValue: true,
        getInitialValueInEffect: false,
    })

    // Session Storage is cleared when the page refreshes or the tab is closed
    const [defaultOpenCopilotAnalyzeSession, setDefaultOpenCopilotAnalyzeSession] = useSessionStorage({
        key: "copilot-analyze-default-open",
        defaultValue: true,
        getInitialValueInEffect: false,
    })

    const createNewSession = () => {
        const sessionId = uuidv7()
        const currentSessions = queryClient.getQueryData<CopilotChatsInterface>(copilotKeys.sessions())

        // Find most recent session by last_active
        const lastSession =
            currentSessions &&
            Object.entries(currentSessions).sort(
                ([, a], [, b]) => new Date(b.last_active).getTime() - new Date(a.last_active).getTime(),
            )[0]?.[1]

        // If last session exists and has no messages, update its ID
        if (lastSession && Object.keys(lastSession.messages).length === 0) {
            queryClient.setQueryData<CopilotChatsInterface>(copilotKeys.sessions(), (prevData) => {
                const { [lastSession.id]: oldSession, ...restSessions } = prevData || {}
                return {
                    ...restSessions,
                    [sessionId]: {
                        ...oldSession,
                        id: sessionId,
                        last_active: new Date().toISOString(),
                    },
                }
            })
        } else {
            // Create new session if last session has messages or doesn't exist
            queryClient.setQueryData<CopilotChatsInterface>(copilotKeys.sessions(), (prevData) => ({
                ...prevData,
                [sessionId]: {
                    id: sessionId,
                    label: "New Chat",
                    messages: {},
                    last_active: new Date().toISOString(),
                },
            }))
        }

        if (!context.isFullscreenOpen) {
            context.setIsSessionsListOpen(false)
        }
        context.setActiveSessionId(sessionId)
    }

    const toggleChat = ({ entryPoint = "", shouldCreateNewSession = false } = {}) => {
        setIsOpen((lastIsOpen) => {
            if (!lastIsOpen && shouldCreateNewSession) {
                createNewSession()
            }
            return !lastIsOpen
        })
        setEntryPoint(entryPoint)
    }

    // Close on mobile devices
    useLayoutEffect(() => {
        if (isMobile) {
            setIsOpen(false)
        }
    }, [isMobile, setIsOpen])

    // Handle automatic opening based on data type and feature flags
    useLayoutEffect(() => {
        const shouldAutoOpenForBillOrEvent =
            !isMobile && ([QuorumDataType.bill.key, QuorumDataType.event.key] as string[]).includes(params?.dataType)

        const shouldAutoOpenForAITrackingBoard = !isMobile && aiTrackingBoardMatch

        if (shouldAutoOpenForBillOrEvent) {
            if (isFeatureEnabled("ff_copilot_always_open_on_bill_profile")) {
                setIsOpen(true)
                setEntryPoint("")
            } else if (isFeatureEnabled("ff_copilot_open_on_bill_profile_once_per_session")) {
                if (defaultOpenCopilotAnalyzeSession) {
                    setIsOpen(true)
                    setEntryPoint("")
                    setDefaultOpenCopilotAnalyzeSession(false)
                }
            } else {
                if (defaultOpenCopilotAnalyze) {
                    setIsOpen(true)
                    setEntryPoint("")
                    setDefaultOpenCopilotAnalyze(false)
                }
            }
        }

        if (shouldAutoOpenForAITrackingBoard) {
            if (defaultOpenCopilotAnalyzeSession) {
                setIsOpen(true)
                setEntryPoint("")
                setDefaultOpenCopilotAnalyzeSession(false)
            } else if (defaultOpenCopilotAnalyze) {
                setIsOpen(true)
                setEntryPoint("")
                setDefaultOpenCopilotAnalyze(false)
            }
        }
    }, [
        setEntryPoint,
        defaultOpenCopilotAnalyze,
        defaultOpenCopilotAnalyzeSession,
        isMobile,
        params?.dataType,
        aiTrackingBoardMatch,
        setDefaultOpenCopilotAnalyze,
        setDefaultOpenCopilotAnalyzeSession,
        setIsOpen,
    ])

    return {
        isOpen,
        entryPoint,
        toggleChat,
        createNewSession,
    }
}
